import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faDollarSign,
} from '@fortawesome/pro-regular-svg-icons';
import type { CarrierRuleOutcome } from '../../../../../../../api/quotes.api';
import {
  formatMonetaryReason,
  isMonetaryRule,
  ruleLabel,
} from '../carrierEligibilityDisplay';
import { failColor, passColor } from './statusTone';

interface CarrierEligibilityBreakdownProps {
  readonly rules: CarrierRuleOutcome[];
  readonly dark: boolean;
}

/** Per-rule pass/fail breakdown shown inside a carrier row's expanded disclosure (FPX-7027). */
const CarrierEligibilityBreakdown = ({
  rules,
  dark,
}: CarrierEligibilityBreakdownProps) => (
  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
    {rules.map((rule, index) => (
      <CarrierRuleLine
        key={rule.rule}
        rule={rule}
        withDivider={index > 0}
        dark={dark}
      />
    ))}
  </ul>
);

interface CarrierRuleLineProps {
  readonly rule: CarrierRuleOutcome;
  readonly withDivider: boolean;
  readonly dark: boolean;
}

const CarrierRuleLine = ({ rule, withDivider, dark }: CarrierRuleLineProps) => {
  const passed = rule.outcome === 'PASS';
  const monetary = isMonetaryRule(rule.rule);
  const reason =
    rule.reason && monetary ? formatMonetaryReason(rule.reason) : rule.reason;
  const color = passed ? passColor(dark) : failColor(dark);

  return (
    <li
      style={{
        display: 'flex',
        gap: 8,
        padding: '6px 0',
        borderTop: withDivider ? '1px solid rgba(128, 128, 128, 0.16)' : 'none',
      }}
    >
      <FontAwesomeIcon
        icon={passed ? faCircleCheck : faCircleXmark}
        style={{ color, fontSize: 14, marginTop: 2, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            {ruleLabel(rule.rule)}
          </span>
          {monetary ? (
            <FontAwesomeIcon
              icon={faDollarSign}
              title="Monetary rule"
              style={{ fontSize: 10, opacity: 0.45 }}
            />
          ) : null}
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.04em',
              color,
            }}
          >
            {rule.outcome}
          </span>
        </div>
        {reason ? (
          <p
            style={{
              margin: '2px 0 0',
              fontSize: 12,
              lineHeight: 1.35,
              opacity: 0.7,
            }}
          >
            {reason}
          </p>
        ) : null}
      </div>
    </li>
  );
};

export default CarrierEligibilityBreakdown;
